import React, { useEffect, useContext, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { Toolbar } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { CgDetailsMore, CgListTree } from 'react-icons/all';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import * as _ from 'lodash';
import PermissionsComponent from 'components/Wrappers/PermissionsComponent';
import { useHistory } from 'react-router';
import usePermissions from 'hooks/permissions-hooks';
import actions from 'roles-actions-permissions/actions';
import moment from 'moment';
import { useItem } from 'hooks/atoms/item-hook';
import { useDialog } from 'dialog-provider-hooks';
import CollectionKeys from 'hooks/molecules/collection-keys';
import AppLoader from '../../basic/AppLoader';
import { ThematicPackageService } from '../../../services';
import CollectionItemHeader from '../../layout/CollectionItemHeader';
import getTheme from '../../../theme/theme';
import AppButtonMinimal from '../../basic/AppButtonMinimal';
import BasicField from '../../basic/BasicField';
import ItemsDropdownButton from '../ItemsDropdownButton';
import { ThematicPackagePageCtx } from '../../../pages/ThematicPage';
import { useAlerts } from '../../../hooks/alerts-hooks';
import ThematicPackageRoadmap from './ThematicPackageRoadmap';

function ThematicPackageDetails(props) {
  const {
    thematicPackage,
    setThematicPackage,
  } = useContext(ThematicPackagePageCtx);

  const {
    deleteItem: deleteThematicPackage,
  } = useItem(CollectionKeys.THEMATIC_PACKAGES, props.id);

  const { openDialog } = useDialog();

  const openDeleteThematicDialog = () => {
    openDialog({
      title: 'Delete Thematic Package?',
      body() {
        return (<p>Are you sure you want to delete Thematic Package</p>);
      },
      onSubmit() {
        deleteThematicPackage({ id: props.id });
        history.push({
          pathname: '/thematic',
        });
      },
    });
  };

  const { alertSuccess, alertError } = useAlerts();

  const queryClient = useQueryClient();

  const [ taxerror, setTaxError ] = useState(false);
  const [ rerror, setRerror ] = useState(false);

  const { hasPermissions } = usePermissions();

  const thematicPackageBasicDetails = [
    {
      field: 'name',
      label: 'Name',
      disabled: !hasPermissions(actions.EDIT_NAME),
    },
    {
      field: 'description',
      label: 'Description',
      disabled: !hasPermissions(actions.EDIT_DESCRIPTION),
    },
    {
      field: 'prod_code',
      label: 'Prod Code',
      disabled: !hasPermissions(actions.EDIT_PRODUCT_CODE),
    },
  ];

  const history = useHistory();

  const thematicPackagePublishingDetails = [
    {
      field: 'updated_at',
      label: 'Last updated',
      disabled: true,
    },
    {
      field: 'published_at',
      label: 'Published',
      disabled: true,
    },
    {
      field: 'created_at',
      label: 'Created',
      disabled: true,
    },
  ];

  const numRegex = /^[+-]?\d+(\.\d+)?$|^$/;

  const re = /(^100([.]0{1,2})?)$|(^\d{1,2}([.]\d{1,2})?)$|^$/;

  const saveMutation = useMutation((t) => {
    return ThematicPackageService.update(t);
  },
  {
    onSuccess: () => {
      alertSuccess('Changes successfully saved');
    },
    onError: (err) => {
      console.log(err);
      alertError('Changes did not saved');
    },
    onSettled: () => {
      // Invalidate and refetch
      return queryClient.invalidateQueries([ 'thematic-package', props.id ]);
    },
  });

  function handleOnSave(e) {
    e.preventDefault();

    if ([ taxerror, rerror ].some((er) => {
      return er;
    })) {
      alertError('please check your input');
      return;
    }

    const thematicPackageClone = _.clone(thematicPackage);
    // eslint-disable-next-line no-restricted-syntax
    for (const [ key, value ] of Object.entries(thematicPackageClone)) {
      if ('object' === typeof value && null !== value) {
        thematicPackageClone[key] = value.id;
      }
    }
    saveMutation.mutate(thematicPackageClone);
  }

  const { isLoading, data, isError } = useQuery([ 'thematic-package', props.id ],
    () => {
      return ThematicPackageService.getById(props.id);
    });

  useEffect(() => {
    setThematicPackage((prevThematicPackage) => {
      if (_.isEqual(data, prevThematicPackage)) {
        return prevThematicPackage;
      }

      return data;
    });
  }, [ data, setThematicPackage ]);

  const onThematicPackagePropertyChange = (key, val) => {
    setThematicPackage({ ...thematicPackage, [key]: val });
  };

  // eslint-disable-next-line no-unused-vars
  const goToThematicPackageTraits = () => {
    history.push({
      pathname: '/thematic',
      search: `?id=${ props.id }&pid=Traits`,
    });
  };

  const goToReportBuilder = () => {
    history.push({
      pathname: '/thematic',
      search: `?id=${ props.id }&pid=Report`,
    });
  };

  // eslint-disable-next-line no-unused-vars
  const goToAssociationReports = () => {
    history.push({
      pathname: '/association-reports',
      search: `?thematic-package-id=${ props.id }`,
    });
  };

  if (isLoading) {
    return <AppLoader />;
  }

  if (!thematicPackage) {
    return <AppLoader />;
  }

  if (isError) {
    return <div>Thematic Package Not found</div>;
  }

  return (
    <>
      <CollectionItemHeader
        title={ `Name: ${ thematicPackage.name }` }
        subtitle={ `Description: ${ thematicPackage.description }` }
        actions={ (
          <>
            <div />
            <Grid container spacing={ 1 } justify='flex-end'>
              <Grid item>
                <AppButtonMinimal
                  text='Report builder'
                  onClick={ () => { return goToReportBuilder(); } }
                />
              </Grid>
              <PermissionsComponent permissions={ actions.SHOW_TRAITS }>
                <Grid item>
                  <AppButtonMinimal
                    text='Association reports'
                    onClick={ () => { return goToAssociationReports(); } }
                  />
                </Grid>
              </PermissionsComponent>
              <PermissionsComponent permissions={ actions.SHOW_TRAITS }>
                <Grid item>
                  <AppButtonMinimal
                    text='Edit thematic package traits'
                    onClick={ () => { return goToThematicPackageTraits(); } }
                  />
                </Grid>
              </PermissionsComponent>
              <PermissionsComponent permissions={ actions.DELETE_THEMATIC_PACKAGE }>
                <Grid item>
                  <AppButtonMinimal
                    color={ getTheme().palette.error.main }
                    text='Delete'
                    onClick={ openDeleteThematicDialog }
                  />
                </Grid>
              </PermissionsComponent>
            </Grid>
          </>
        ) }
      />
      <Toolbar />
      <Grid container spacing={ 3 }>
        <Grid item xs={ 12 } md={ 8 }>
          <Grid container spacing={ 3 } direction='column'>
            <Grid item>
              <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: getTheme().spacing(2) } }>
                <div style={ { display: 'flex', alignItems: 'center', flexDirection: 'row' } }>
                  <CgDetailsMore style={ { marginRight: getTheme().spacing(2) } } size={ 24 } />
                  <Typography variant='h6'>
                    Thematic Package Details
                  </Typography>
                </div>
                <div style={ { display: 'flex', alignItems: 'center', flexDirection: 'row' } }>
                  <div style={ { width: getTheme().spacing(1) } } />
                  <PermissionsComponent permissions={ actions.RESET_THEMATIC_PACKAGE }>
                    <AppButtonMinimal
                      text='Reset'
                      color={ getTheme().palette.warning.main }
                      disabled={ _.isEqual(data, thematicPackage) }
                      onClick={ () => {
                        return setThematicPackage(data);
                      } }
                    />
                  </PermissionsComponent>
                  <div style={ { width: getTheme().spacing(1) } } />
                  <PermissionsComponent permissions={ actions.SAVE_THEMATIC_PACKAGE }>
                    <AppButtonMinimal
                      text={ saveMutation.isLoading ? 'Saving ...' : 'Save' }
                      disabled={ _.isEqual(data, thematicPackage) || saveMutation.isLoading }
                      onClick={ (e) => {
                        return handleOnSave(e, thematicPackage);
                      } }
                    />
                  </PermissionsComponent>
                </div>
              </div>
              <Card>
                <Grid container spacing={ 3 }>
                  <Grid item xs={ 6 }>
                    <CardContent>
                      {
                        thematicPackageBasicDetails.map(({ field, label, disabled }) => {
                          return (
                            <BasicField
                              key={ field }
                              onChange={ (v) => {
                                return onThematicPackagePropertyChange(field, v);
                              } }
                              disabled={ disabled }
                              title={ label }
                              field={ thematicPackage[field] }
                            />
                          );
                        })
                      }
                      <ItemsDropdownButton
                        onChange={ (v) => {
                          return onThematicPackagePropertyChange('race_id', v);
                        } }
                        disabled={ !hasPermissions(actions.EDIT_POPULATION_GROUP) }
                        title='Population Group'
                        collection='races'
                        defaultvalue={ thematicPackage.race_id }
                      />
                      <PermissionsComponent permissions={ actions.EDIT_DEVICES }>
                        <ItemsDropdownButton
                          onChange={ (v) => {
                            return onThematicPackagePropertyChange('device_id', v);
                          } }
                          title='Device'
                          collection='devices'
                          defaultvalue={ thematicPackage.device_id }
                        />
                      </PermissionsComponent>
                    </CardContent>
                  </Grid>
                  <Grid item xs={ 6 }>
                    <CardContent>
                      {
                        thematicPackagePublishingDetails.map(({ field, label, disabled }) => {
                          return (
                            <BasicField
                              key={ label }
                              disabled={ disabled }
                              title={ label }
                              field={ moment(`${ thematicPackage[field] }`).format(' h:mm a MMMM DD, YYYY') }
                            />
                          );
                        })
                      }
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item>
              <Card>
                <Grid container spacing={ 3 }>
                  <Grid item xs={ 6 }>
                    <CardContent>
                      <ItemsDropdownButton
                        onChange={ (v) => {
                          return onThematicPackagePropertyChange('questionnaire_id', v);
                        } }
                        disabled={ !hasPermissions(actions.EDIT_QUESTIONNAIRE) }
                        title='Questionnaire'
                        collection='questionnaires'
                        defaultvalue={ thematicPackage.questionnaire_id }
                      />
                      <ItemsDropdownButton
                        onChange={ (v) => {
                          return onThematicPackagePropertyChange('thematic_category_id', v);
                        } }
                        disabled={ !hasPermissions(actions.EDIT_THEMATIC_GROUP) }
                        title='Thematic Categories'
                        collection='thematic-categories'
                        defaultvalue={ thematicPackage.thematic_category_id }
                      />
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item xs>
              <Card>
                <CardContent>
                  <BasicField
                    key='measurement_unit'
                    onChange={ (v) => {
                      return onThematicPackagePropertyChange('measurement_unit', v);
                    } }
                    disabled={ !hasPermissions(actions.EDIT_MEASUREMENT_UNIT) }
                    title='Measurement Unit'
                    field={ thematicPackage.measurement_unit }
                  />
                  <BasicField
                    key='tax'
                    onChange={ (v) => {
                      setTaxError(!re.test(v));
                      return onThematicPackagePropertyChange('tax', v);
                    } }
                    disabled={ !hasPermissions(actions.EDIT_TAX) }
                    title='Taxation'
                    field={ thematicPackage.tax }
                    helperText={ taxerror ? 'This field requires only numbers in range 0-100' : '' }
                    error={ taxerror }
                  />
                  <BasicField
                    key='accounting_category'
                    onChange={ (v) => {
                      return onThematicPackagePropertyChange('accounting_category', v);
                    } }
                    disabled={ !hasPermissions(actions.EDIT_ACCOUNTING_CATEGORY) }
                    title='Accounting Category'
                    field={ thematicPackage.accounting_category }
                  />
                  <BasicField
                    key='barcode'
                    onChange={ (v) => {
                      return onThematicPackagePropertyChange('barcode', v);
                    } }
                    disabled={ !hasPermissions(actions.EDIT_BARCODE) }
                    title='Barcode'
                    field={ thematicPackage.barcode }
                  />
                  <BasicField
                    key='business_unit'
                    onChange={ (v) => {
                      return onThematicPackagePropertyChange('business_unit', v);
                    } }
                    disabled={ !hasPermissions(actions.EDIT_BUSINESS_UNIT) }
                    title='Business Unit'
                    field={ thematicPackage.business_unit }
                  />
                  <BasicField
                    key='kit_price'
                    onChange={ (v) => {
                      setRerror(!numRegex.test(v));
                      return onThematicPackagePropertyChange('kit_price', v);
                    } }
                    disabled={ !hasPermissions(actions.EDIT_RETAIL_PRICE) }
                    title='Kit price'
                    field={ thematicPackage.kit_price }
                    helperText={ rerror ? 'This field requires only numbers' : '' }
                    error={ rerror }
                  />
                  <BasicField
                    key='report_price'
                    onChange={ (v) => {
                      setRerror(!numRegex.test(v));
                      return onThematicPackagePropertyChange('report_price', v);
                    } }
                    disabled={ !hasPermissions(actions.EDIT_RETAIL_PRICE) }
                    title='Report price'
                    field={ thematicPackage.report_price }
                    helperText={ rerror ? 'This field requires only numbers' : '' }
                    error={ rerror }
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={ 12 } md={ 4 }>
          <Grid container spacing={ 3 } direction='column'>
            <Grid item xs={ 12 }>
              <div style={ { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: getTheme().spacing(2) } }>
                <div style={ { display: 'flex', alignItems: 'center', flexDirection: 'row' } }>
                  <CgListTree style={ { marginRight: getTheme().spacing(2) } } size={ 24 } />
                  <Typography variant='h6'>
                    Roadmap status
                  </Typography>
                </div>
              </div>
              <Card>
                <ThematicPackageRoadmap id={ props.id } />
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default ThematicPackageDetails;
